/* eslint-disable func-call-spacing */
import { cart } from '../helpers/cart'

export class GiftCardForm extends HTMLElement {
  private form: HTMLFormElement

  // Field validators
  private validators = new Map<string, (value: string) => boolean>([
    ['email', function(value) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      return emailRegex.test(value) && /^[\x20-\x7F]*$/.test(value)
    }],
    ['generic', function(value) {
      // Basic validation - non-empty and only printable characters
      return value.trim().length > 0 && /^[\x20-\x7F]*$/.test(value)
    }]
  ])

  constructor() {
    super()
    // Create internal form element
    this.form = document.createElement('form')
    // Move all children to the form
    while (this.firstChild) {
      this.form.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to form
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.form.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.form)
    this.form.addEventListener('submit', this.handleProductAdd.bind(this))

    // Initialize character counter
    this.initializeCharacterCounter()

    // Initialize variant selection listener
    this.initializeVariantListener()
  }

  private initializeCharacterCounter() {
    const messageInput = this.form.querySelector('[data-char-counter]') as HTMLInputElement
    const counterDisplay = this.form.querySelector('[data-char-counter-display]') as HTMLElement

    if (messageInput && counterDisplay) {
      const updateCounter = () => {
        const currentLength = messageInput.value.length
        const maxLength = messageInput.maxLength || 200
        counterDisplay.textContent = `${currentLength}/${maxLength} characters`
      }

      // Set initial count
      updateCounter()

      // Add event listeners for real-time updates
      messageInput.addEventListener('input', updateCounter)
      messageInput.addEventListener('keyup', updateCounter)
      messageInput.addEventListener('paste', updateCounter)
    }
  }

  private initializeVariantListener() {
    // Find the selected value display span outside the form (in the parent page)
    const selectedValueSpan = document.querySelector('.gift-card-selected-value') as HTMLElement
    const mainPriceSpan = document.querySelector('.gift-card-main-price') as HTMLElement
    if (!selectedValueSpan) return
    if (!mainPriceSpan) return

    // Listen for changes on the hidden select element that gets updated by variant-selector
    const hiddenSelect = this.form.querySelector('select[name="id"]') as HTMLSelectElement

    if (hiddenSelect) {
      hiddenSelect.addEventListener('change', () => {
        const selectedOption = hiddenSelect.options[hiddenSelect.selectedIndex]
        if (selectedOption && selectedOption.text) {
          selectedValueSpan.textContent = selectedOption.text
          mainPriceSpan.textContent = selectedOption.text
        }
      })

      // Set initial value if a variant is already selected
      const selectedOption = hiddenSelect.options[hiddenSelect.selectedIndex]
      if (selectedOption && selectedOption.text && selectedOption.value) {
        selectedValueSpan.textContent = selectedOption.text
        mainPriceSpan.textContent = selectedOption.text
      }
    }
  }

  private validateForm(): { valid: boolean, errors: Record<string, string>, fields: Record<string, string> } {
    const errors: Record<string, string> = {}
    let valid = true
    const fields: Record<string, string> = {}

    // Get all form fields that start with properties[
    const propertyFields = this.form.querySelectorAll('[name^="properties["]')
    propertyFields.forEach(field => {
      const input = field as HTMLInputElement | HTMLTextAreaElement
      const name = input.name
      const value = input.value || ''
      fields[name] = value

      // Skip validation for empty message fields
      if (name.toLowerCase().includes('message') && !value) return

      // Use email validator for email fields, generic validator for everything else
      const validator = name.toLowerCase().includes('email') ? 'email' : 'generic'
      if (!this.validators.get(validator)!(value)) {
        errors[name] = 'Please enter a valid value'
        valid = false
      }
    })

    return { valid, errors, fields }
  }

  private showValidationErrors(errors: Record<string, string>) {
    for (const [field, message] of Object.entries(errors)) {
      const inputElement = this.form.querySelector(`[name="${field}"]`)
      if (inputElement) {
        const errorElement = document.createElement('div')
        errorElement.className = 'form-error w-full paragraph-s text-system-error pt-0.5 pb-0.75'
        errorElement.textContent = message
        inputElement.parentNode?.insertBefore(errorElement, inputElement.nextSibling)
      }
    }
  }

  private clearValidationErrors() {
    this.form.querySelectorAll('.form-error').forEach(el => el.remove())
  }

  async handleProductAdd(event: SubmitEvent) {
    event.preventDefault()
    const form = new FormData(this.form)
    this.clearValidationErrors()

    // Validate the form
    const validation = this.validateForm()
    if (!validation.valid) {
      this.showValidationErrors(validation.errors)
      return
    }

    const quantityValue = form.get('quantity')
    const idValue = form.get('id')

    const quantity = quantityValue ? parseInt(quantityValue.toString()) : 0
    const id = idValue ? parseInt(idValue.toString()) : 0

    if (!quantity || !id) return

    // Create properties object from validated fields
    const propertyObject: { [key: string]: string } = {}
    for (const [name, value] of Object.entries(validation.fields)) {
      if (name.startsWith('properties[') && name.endsWith(']')) {
        const propertyName = name.slice(11, -1)
        propertyObject[propertyName] = value
      }
    }

    const data = await cart.addLineItems([{
      id,
      quantity,
      properties: propertyObject
    }])

    if (data) return

    if (!data) {
      console.error('Failed to add item to cart', data)
    }
  }
}
